import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { fileUrl } from "../lib";

const TZ = "America/Sao_Paulo";
const dataLonga = (iso) => {
  if (!iso) return "Sem data";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("pt-BR", { timeZone: "UTC", day: "2-digit", month: "long", year: "numeric" });
  }
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString("pt-BR", { timeZone: TZ, day: "2-digit", month: "long", year: "numeric" })} · ${d.toLocaleTimeString("pt-BR", { timeZone: TZ, hour: "2-digit", minute: "2-digit" })}`;
  } catch { return "Sem data"; }
};

const navBtn = (lado) => ({
  position: "absolute", top: "50%", [lado]: 12, transform: "translateY(-50%)", zIndex: 3,
  width: 48, height: 48, borderRadius: "50%", border: "1px solid rgba(184,147,90,0.5)",
  background: "rgba(13,13,15,0.6)", color: "#f5f0e8", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
});

/* Visualizador em tela cheia da cronologia do SB: anterior/próxima (setas, teclado, arrastar), data e legenda. */
export function SBMidiaViewer({ itens, index, onIndex, onClose }) {
  const it = itens[index];
  const toque = useRef(null);
  const total = itens.length;
  const ir = useCallback((d) => onIndex((index + d + total) % total), [index, total, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") ir(1);
      if (e.key === "ArrowLeft") ir(-1);
    };
    window.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = overflow; };
  }, [ir, onClose]);

  if (!it) return null;
  const onTouchStart = (e) => { toque.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (toque.current == null) return;
    const dx = e.changedTouches[0].clientX - toque.current;
    if (Math.abs(dx) > 50) ir(dx < 0 ? 1 : -1);
    toque.current = null;
  };

  return createPortal(
    <div data-testid="sb-viewer" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(8,8,10,0.97)", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", gap: 12, color: "#f5f0e8" }}>
        <div>
          <div data-testid="sb-viewer-data" style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 18 }}>{dataLonga(it.data)}</div>
          <div data-testid="sb-viewer-contador" style={{ fontSize: 10, letterSpacing: ".25em", textTransform: "uppercase", color: "#b8935a", marginTop: 4 }}>
            {it.kind === "video" ? "Vídeo" : "Foto"} {index + 1} de {total}
          </div>
        </div>
        <button onClick={onClose} aria-label="Fechar" data-testid="sb-viewer-fechar"
          style={{ ...navBtn("right"), position: "static", transform: "none", width: 40, height: 40 }}><X size={18} /></button>
      </div>
      <div style={{ position: "relative", flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 clamp(8px, 5vw, 72px)" }}>
        {total > 1 && <button onClick={() => ir(-1)} aria-label="Anterior" style={navBtn("left")} data-testid="sb-viewer-anterior"><ChevronLeft size={22} /></button>}
        {it.kind === "video" ? (
          <video key={it.url} src={fileUrl(it.url)} poster={it.thumb ? fileUrl(it.thumb) : undefined} controls autoPlay playsInline
            style={{ maxWidth: "100%", maxHeight: "100%", display: "block" }} data-testid="sb-viewer-video" />
        ) : (
          <img key={it.url} src={fileUrl(it.url)} alt={it.legenda || it.etapa || ""} className="fade-in"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block", boxShadow: "0 12px 60px rgba(0,0,0,0.6)" }} data-testid="sb-viewer-img" />
        )}
        {total > 1 && <button onClick={() => ir(1)} aria-label="Próxima" style={navBtn("right")} data-testid="sb-viewer-proxima"><ChevronRight size={22} /></button>}
      </div>
      <div data-testid="sb-viewer-legenda" style={{ padding: "14px 18px 22px", textAlign: "center", minHeight: 56 }}>
        {it.legenda && <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 16, color: "#f5f0e8" }}>{it.legenda}</div>}
        {it.etapa && <div style={{ fontSize: 10, letterSpacing: ".25em", textTransform: "uppercase", color: "#b8935a", marginTop: 6 }}>{it.etapa}</div>}
      </div>
    </div>,
    document.body,
  );
}
